import { refreshOwnedSession } from './refresh-owned-session';
import { supabase } from './supabaseClient';
import { logger } from '@shared/lib/logger';
import { isBrowserOnline } from '@shared/lib/network-status';
import { recordSlowApi } from '@shared/lib/slow-api-tracker';
import {
  clearDoctorControlStorage,
  getDoctorControlRequestIdFromStorage,
} from '@shared/stores/doctor-control-store';

export interface ApiResponse<T> {
  data: T;
  pagination?: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
  meta?: {
    timestamp: string;
    version: string;
    requestId: string;
  };
}

export interface CacheConfig {
  ttl?: number; // Time to live in milliseconds
  staleWhileRevalidate?: boolean;
  tags?: string[]; // Cache tags for invalidation
}

export interface RequestConfig extends Omit<RequestInit, 'cache' | 'priority'> {
  cache?: CacheConfig;
  timeout?: number;
  retries?: number;
  priority?: 'low' | 'normal' | 'high';
  responseType?: 'json' | 'blob' | 'text';
  skipAuth?: boolean;
  skipProactiveTokenRefresh?: boolean;
  useFreshSessionAuth?: boolean;
  timeoutProfile?:
    | 'default'
    | 'quotingQueue'
    | 'travelPlanning'
    | 'dashboard'
    | 'background'
    | 'generation';
  dedupe?: 'auto' | 'off';
  requestLabel?: string;
  doctorControlRequestId?: string | null;
  skipDoctorControlHeader?: boolean;
}

export interface ApiError {
  error: {
    code: string;
    message: string;
    details?: Record<string, any>;
    stack?: string;
  };
}

export type ApiErrorKind =
  | 'timeout'
  | 'network'
  | 'rate_limit'
  | 'abort'
  | 'auth'
  | 'server'
  | 'client'
  | 'unknown';

export class ApiClientError extends Error {
  status: number;
  code?: string;
  details?: Record<string, any>;
  kind: ApiErrorKind;
  retryable: boolean;

  constructor(
    message: string,
    status: number,
    code?: string,
    details?: Record<string, any>,
    kind?: ApiErrorKind,
    retryable?: boolean
  ) {
    super(message);
    this.name = 'ApiClientError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.kind = kind ?? classifyApiErrorFromStatus(status, code);
    this.retryable = retryable ?? isRetryableErrorKind(this.kind);
  }
}

// Rate-limit errors are deliberately NOT retryable: automatic retries against
// a 429 wall keep the client pinned over the server limit and turn one
// throttled burst into a sustained outage. Pollers re-attempt on their own
// cadence instead.
const isRetryableErrorKind = (kind: ApiErrorKind): boolean =>
  kind === 'timeout' || kind === 'network' || kind === 'server';

const classifyApiErrorFromStatus = (status: number, code?: string): ApiErrorKind => {
  if (code === 'REQUEST_ABORTED') return 'abort';
  if (code === 'REQUEST_TIMEOUT') return 'timeout';
  if (status === 401 || status === 403) return 'auth';
  if (status === 408) return 'timeout';
  if (status === 429) return 'rate_limit';
  if (status >= 500) return 'server';
  if (status >= 400) return 'client';
  if (status === 0) return 'network';
  return 'unknown';
};

/**
 * RFC 7231 §7.1.3 — Retry-After is either delta-seconds (1*DIGIT) or an
 * HTTP-date. Exported for direct unit testing.
 */
export const parseRetryAfterMs = (headerValue: string | null, fallbackMs: number): number => {
  if (!headerValue) return fallbackMs;
  const trimmed = headerValue.trim();
  if (/^\d+$/.test(trimmed)) {
    return parseInt(trimmed, 10) * 1000;
  }
  const ts = Date.parse(trimmed);
  if (!isNaN(ts)) {
    return Math.max(0, ts - Date.now());
  }
  return fallbackMs;
};

export const classifyApiError = (error: unknown): ApiErrorKind => {
  if (error instanceof ApiClientError) return error.kind;
  if (error instanceof DOMException && error.name === 'AbortError') return 'abort';
  // Some runtimes surface fetch cancellations as plain objects with name AbortError
  if (
    typeof error === 'object' &&
    error !== null &&
    (error as { name?: string }).name === 'AbortError'
  ) {
    return 'abort';
  }
  if (error instanceof DOMException && error.name === 'TimeoutError') return 'timeout';
  if (error instanceof TypeError) return 'network';
  return 'unknown';
};

class ApiClient {
  private static readonly ACTIVE_USER_STORAGE_KEY = 'jema_active_user_id_v2';
  private baseUrl: string;
  private tokenRefreshPromise: Promise<void> | null = null;
  private pendingRequests: Map<string, Promise<any>> = new Map();
  private requestCache: Map<string, { data: any; timestamp: number; ttl: number }> = new Map();
  private enableCustomHeaders = false;
  private rateLimitState = {
    requests: 0,
    resetTime: Date.now() + 60000, // Reset every minute
    maxRequests: 1000, // Increased from 100 to 1000 requests per minute
  };
  private readonly timeoutProfiles: Record<NonNullable<RequestConfig['timeoutProfile']>, number> = {
    default: 15_000,
    quotingQueue: 12_000,
    travelPlanning: 20_000,
    dashboard: 8_000,
    background: 30_000,
    generation: 35_000,
  };

  constructor() {
    const rawUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
    const isBrowser = typeof window !== 'undefined';
    const hostname = isBrowser ? window.location.hostname : '';
    const isLocalhostHost = hostname === 'localhost' || hostname === '127.0.0.1';

    // Normalize base URL to always target /api/v2 without duplicating versioned segments.
    const trimTrailingSlash = (value: string) => value.replace(/\/+$/g, '');
    const urlNoTrail = trimTrailingSlash(rawUrl);

    let normalizedBase = urlNoTrail;
    const hasApi = /\/(api)(\/|$)/.test(urlNoTrail);
    const endsWithV2 = /\/v2$/.test(urlNoTrail);
    const apiSegmentRegex = /\/api(?:\/[^?#]*)*(?:[?#].*)?$/;

    if (endsWithV2) {
      normalizedBase = urlNoTrail;
    } else if (hasApi && apiSegmentRegex.test(urlNoTrail)) {
      normalizedBase = urlNoTrail.replace(apiSegmentRegex, '/api/v2');
    } else {
      normalizedBase = urlNoTrail + '/api/v2';
    }

    // In local browser development, prefer same-origin API calls and let Vite proxy
    // `/api/*` to the local gateway. This avoids stale absolute env hosts silently
    // sending requests to a different machine than the one the developer is watching.
    this.baseUrl = isLocalhostHost ? '/api/v2' : normalizedBase;

    // Enable custom headers only if explicitly configured
    this.enableCustomHeaders = import.meta.env.VITE_ENABLE_CUSTOM_HEADERS === 'true';

    // Start cache cleanup interval
    this.startCacheCleanup();
  }

  private async getAuthToken(): Promise<string | null> {
    try {
      const {
        data: { session },
      } = await this.withTimeout(supabase.auth.getSession(), 3000, 'Supabase session read');
      return session?.access_token || this.getPersistedAuthToken();
    } catch (error) {
      logger.warn('Supabase session read timed out; using persisted session token when available', {
        error: error instanceof Error ? error.message : String(error),
      });
      return this.getPersistedAuthToken();
    }
  }

  /**
   * URL and headers for a request that must go out while the page is unloading, where nothing
   * can be awaited: the auth token comes from the persisted session (which Supabase keeps
   * refreshed), not from an async read. Null when there is no usable token.
   */
  buildUnloadRequest(endpoint: string): { url: string; headers: Record<string, string> } | null {
    const token = this.getPersistedAuthToken();
    if (!token) return null;
    return {
      url: `${this.baseUrl}${endpoint}`,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    };
  }

  private withTimeout<T>(promise: Promise<T>, timeoutMs: number, label: string): Promise<T> {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const timeoutPromise = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(() => {
        reject(new Error(`${label} timed out after ${timeoutMs}ms`));
      }, timeoutMs);
    });

    return Promise.race([promise, timeoutPromise]).finally(() => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    });
  }

  private getPersistedAuthToken(): string | null {
    if (typeof window === 'undefined') return null;

    try {
      const storageKey = Object.keys(window.localStorage).find(key =>
        key.startsWith('jema_supabase_auth:')
      );
      if (!storageKey) return null;

      const rawSession = window.localStorage.getItem(storageKey);
      if (!rawSession) return null;

      const parsed = JSON.parse(rawSession);
      const session = parsed?.currentSession ?? parsed?.session ?? parsed;
      const token = typeof session?.access_token === 'string' ? session.access_token : null;
      const expiresAt = typeof session?.expires_at === 'number' ? session.expires_at * 1000 : null;

      if (!token) return null;
      if (expiresAt && expiresAt <= Date.now() + 10_000) return null;

      return token;
    } catch (error) {
      logger.warn('Failed to read persisted Supabase auth token', { error });
      return null;
    }
  }

  private getExplicitAuthorizationHeader(headers?: HeadersInit): string | null {
    if (!headers) return null;

    if (headers instanceof Headers) {
      return headers.get('Authorization');
    }

    if (Array.isArray(headers)) {
      const match = headers.find(([key]) => key.toLowerCase() === 'authorization');
      return match?.[1] ?? null;
    }

    for (const [key, value] of Object.entries(headers)) {
      if (key.toLowerCase() === 'authorization') {
        return typeof value === 'string' ? value : null;
      }
    }

    return null;
  }

  private isInvalidRefreshTokenError(error: unknown): boolean {
    const message = String((error as any)?.message ?? '').toLowerCase();
    const code = String((error as any)?.code ?? '').toLowerCase();
    return (
      message.includes('invalid refresh token') ||
      message.includes('refresh token not found') ||
      message.includes('jwt expired') ||
      code.includes('refresh_token')
    );
  }

  private async clearInvalidSession(reason: string, error?: unknown): Promise<void> {
    logger.warn('Clearing invalid local auth session', {
      reason,
      error: (error as any)?.message ?? String(error ?? ''),
    });

    try {
      await supabase.auth.signOut({ scope: 'local' } as any);
    } catch {
      // no-op
    }

    try {
      localStorage.removeItem('jema_auth_store_v2');
      localStorage.removeItem('jema_auth_state');
      localStorage.removeItem('userId');
    } catch {
      // no-op
    }
  }

  private async refreshTokenIfNeeded(): Promise<void> {
    // If a refresh is already in progress, wait for it
    if (this.tokenRefreshPromise) {
      await this.tokenRefreshPromise;
      return;
    }

    let session: Awaited<ReturnType<typeof supabase.auth.getSession>>['data']['session'] = null;
    try {
      const result = await this.withTimeout(
        supabase.auth.getSession(),
        3000,
        'Supabase session read'
      );
      session = result.data.session;
    } catch (error) {
      logger.warn('Skipping proactive token refresh after slow Supabase session read', {
        error: error instanceof Error ? error.message : String(error),
      });
      return;
    }

    if (session && session.expires_at) {
      const expiresAt = session.expires_at * 1000; // Convert to milliseconds
      const now = Date.now();
      const tenMinutes = 10 * 60 * 1000; // Increased buffer to 10 minutes

      // Refresh if token expires in less than 10 minutes
      if (expiresAt - now < tenMinutes) {
        this.tokenRefreshPromise = this.withTimeout(
          refreshOwnedSession(),
          5000,
          'Proactive token refresh'
        )
          .then(async ({ error }) => {
            if (!error) {
              return;
            }

            if (this.isInvalidRefreshTokenError(error)) {
              await this.clearInvalidSession('proactive_refresh_failed', error);
              throw new ApiClientError(
                'Authentication required. Please sign in again.',
                401,
                'AUTHENTICATION_REQUIRED',
                undefined,
                'auth',
                false
              );
            }

            logger.error('Token refresh failed', error, { stage: 'refreshTokenIfNeeded' });
            throw error;
          })
          .catch(error => {
            if (
              String((error as Error)?.message ?? error).includes(
                'Proactive token refresh timed out'
              )
            ) {
              logger.warn(
                'Proactive token refresh timed out; continuing with current session token',
                {
                  timeoutMs: 5000,
                }
              );
              return;
            }
            throw error;
          })
          .finally(() => {
            this.tokenRefreshPromise = null;
          });

        await this.tokenRefreshPromise;
      }
    }
  }

  private startCacheCleanup(): void {
    setInterval(() => {
      const now = Date.now();
      for (const [key, entry] of this.requestCache.entries()) {
        if (now > entry.timestamp + entry.ttl) {
          this.requestCache.delete(key);
        }
      }
    }, 60000); // Clean up every minute
  }

  private checkRateLimit(): boolean {
    const now = Date.now();

    // Reset rate limit if time window has passed
    if (now > this.rateLimitState.resetTime) {
      this.rateLimitState.requests = 0;
      this.rateLimitState.resetTime = now + 60000;
    }

    return this.rateLimitState.requests < this.rateLimitState.maxRequests;
  }

  private getRequestScopeKey(): string {
    if (typeof window === 'undefined') {
      return 'scope:ssr';
    }

    try {
      const activeUserId = window.localStorage.getItem(ApiClient.ACTIVE_USER_STORAGE_KEY);
      const doctorControlRequestId = getDoctorControlRequestIdFromStorage();
      if (activeUserId && doctorControlRequestId) {
        return `scope:user:${activeUserId}:doctor-control:${doctorControlRequestId}`;
      }
      if (activeUserId) {
        return `scope:user:${activeUserId}`;
      }
    } catch (error) {
      logger.warn('Failed to read active user id for request scoping', { error });
    }

    return 'scope:anon';
  }

  private getCacheKey(
    endpoint: string,
    options: Pick<RequestConfig, 'method' | 'body'>,
    scopeKey: string
  ): string {
    const method = options.method || 'GET';
    const body = options.body ? JSON.stringify(options.body) : '';
    return `${scopeKey}:${method}:${endpoint}:${body}`;
  }

  private resolveTimeout(profile: RequestConfig['timeoutProfile'], endpoint: string): number {
    if (profile) return this.timeoutProfiles[profile];
    if (endpoint.includes('/quoting-queue')) return this.timeoutProfiles.quotingQueue;
    if (endpoint.includes('/travel-planning')) return this.timeoutProfiles.travelPlanning;
    if (endpoint.includes('/dashboard')) return this.timeoutProfiles.dashboard;
    return this.timeoutProfiles.default;
  }

  private normalizeEndpointForDedup(endpoint: string): string {
    const [path, queryString = ''] = endpoint.split('?');
    if (!queryString) return path;
    const params = new URLSearchParams(queryString);
    const sorted = Array.from(params.entries()).sort(([a], [b]) => a.localeCompare(b));
    const normalizedParams = new URLSearchParams();
    sorted.forEach(([key, value]) => normalizedParams.append(key, value));
    const normalizedQuery = normalizedParams.toString();
    return normalizedQuery ? `${path}?${normalizedQuery}` : path;
  }

  private createRequestSignal(timeoutMs: number, externalSignal?: AbortSignal | null) {
    const controller = new AbortController();
    let timedOut = false;
    const timeoutId = setTimeout(() => {
      timedOut = true;
      controller.abort(new DOMException('Request timed out', 'TimeoutError'));
    }, timeoutMs);

    const handleExternalAbort = () => {
      controller.abort((externalSignal as AbortSignal).reason);
    };

    if (externalSignal) {
      if (externalSignal.aborted) {
        controller.abort(externalSignal.reason);
      } else {
        externalSignal.addEventListener('abort', handleExternalAbort, { once: true });
      }
    }

    return {
      signal: controller.signal,
      didTimeout: () => timedOut,
      cleanup: () => {
        clearTimeout(timeoutId);
        if (externalSignal) {
          externalSignal.removeEventListener('abort', handleExternalAbort);
        }
      },
    };
  }

  private getFromCache(cacheKey: string): any | null {
    const entry = this.requestCache.get(cacheKey);
    if (!entry) return null;

    const now = Date.now();
    if (now > entry.timestamp + entry.ttl) {
      this.requestCache.delete(cacheKey);
      return null;
    }

    return entry.data;
  }

  private setCache(cacheKey: string, data: any, ttl: number): void {
    this.requestCache.set(cacheKey, {
      data,
      timestamp: Date.now(),
      ttl,
    });
  }

  public invalidateCache(pattern?: string): void {
    if (!pattern) {
      this.requestCache.clear();
      return;
    }

    for (const key of this.requestCache.keys()) {
      if (key.includes(pattern)) {
        this.requestCache.delete(key);
      }
    }
  }

  public clearSessionBoundState(reason: string): void {
    const requestCacheSize = this.requestCache.size;
    const pendingRequestCount = this.pendingRequests.size;

    this.requestCache.clear();
    this.pendingRequests.clear();

    logger.info('Cleared API client session-bound state', {
      reason,
      requestCacheSize,
      pendingRequestCount,
    });
  }

  /**
   * Configure whether to send custom headers (X-Request-ID, X-Client-Version)
   * Disable this if you encounter CORS issues
   */
  public setCustomHeadersEnabled(enabled: boolean): void {
    this.enableCustomHeaders = enabled;
  }

  /**
   * Get current custom headers configuration
   */
  public isCustomHeadersEnabled(): boolean {
    return this.enableCustomHeaders;
  }

  private shouldWarnForRecoverableAuthContextFailure(
    endpoint: string,
    requestLabel: string | undefined,
    kind: ApiErrorKind,
    status?: number
  ): boolean {
    const isAuthContextRequest =
      endpoint === '/auth/context' ||
      requestLabel === 'auth-context:get-current-context' ||
      Boolean(requestLabel?.startsWith('auth-context:'));

    return (
      isAuthContextRequest &&
      (kind === 'auth' ||
        kind === 'timeout' ||
        kind === 'network' ||
        status === 401 ||
        status === 408)
    );
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestConfig = {},
    retryCount = 0
  ): Promise<T> {
    const {
      cache,
      timeout,
      retries: _retries = 3,
      priority: _priority = 'normal',
      timeoutProfile,
      dedupe = 'auto',
      requestLabel,
      ...requestOptions
    } = options;

    if (!isBrowserOnline()) {
      throw new ApiClientError(
        "You're offline. We'll reconnect automatically when your connection returns.",
        0,
        'OFFLINE',
        undefined,
        'network',
        false
      );
    }

    const resolvedTimeout = timeout ?? this.resolveTimeout(timeoutProfile, endpoint);
    const scopeKey = this.getRequestScopeKey();

    // Create cache key
    const cacheKey = this.getCacheKey(endpoint, requestOptions, scopeKey);

    // Check cache first for GET requests
    if (requestOptions.method === 'GET' || !requestOptions.method) {
      const cachedData = this.getFromCache(cacheKey);
      if (cachedData) {
        return cachedData;
      }
    }

    const method = (requestOptions.method || 'GET').toUpperCase();
    const shouldDedupe = method === 'GET' && dedupe !== 'off';
    const requestKey = shouldDedupe
      ? `${scopeKey}:${method}:${this.normalizeEndpointForDedup(endpoint)}`
      : '';

    if (shouldDedupe) {
      const pendingRequest = this.pendingRequests.get(requestKey);
      if (pendingRequest) {
        return pendingRequest;
      }
    }

    const requestPromise = this.executeRequest<T>(
      endpoint,
      requestOptions,
      retryCount,
      resolvedTimeout,
      requestLabel
    );

    if (shouldDedupe) {
      this.pendingRequests.set(requestKey, requestPromise);
    }

    try {
      const result = await requestPromise;

      // Cache GET requests if cache config is provided
      if ((requestOptions.method === 'GET' || !requestOptions.method) && cache?.ttl) {
        this.setCache(cacheKey, result, cache.ttl);
      }

      return result;
    } finally {
      // Clean up the pending request
      if (shouldDedupe) {
        this.pendingRequests.delete(requestKey);
      }
    }
  }

  private async executeRequest<T>(
    endpoint: string,
    options: RequestConfig = {},
    retryCount = 0,
    timeout = 30000,
    requestLabel?: string
  ): Promise<T> {
    const startTime = Date.now();
    const requestSignal = this.createRequestSignal(timeout, options.signal ?? null);
    const method = options.method || 'GET';

    try {
      // Only check and charge quota on the first attempt; retries are part of
      // the same logical request and must not consume additional quota slots.
      if (retryCount === 0) {
        if (!this.checkRateLimit()) {
          throw new ApiClientError(
            'Rate limit exceeded. Please try again later.',
            429,
            'RATE_LIMIT_EXCEEDED',
            undefined,
            'rate_limit',
            true
          );
        }
        this.rateLimitState.requests++;
      }

      const explicitAuthorizationHeader = this.getExplicitAuthorizationHeader(options.headers);
      const shouldSkipAuth = options.skipAuth === true;
      const shouldSkipProactiveTokenRefresh = options.skipProactiveTokenRefresh === true;
      const shouldUseFreshSessionAuth = options.useFreshSessionAuth === true;

      // Ensure the ambient session token is fresh before we use it. Requests
      // that provide their own Authorization header can opt into using the
      // latest refreshed session token instead.
      if (
        !shouldSkipAuth &&
        !shouldSkipProactiveTokenRefresh &&
        (!explicitAuthorizationHeader || shouldUseFreshSessionAuth)
      ) {
        await this.refreshTokenIfNeeded();
      }

      let token: string | null = null;
      if (!shouldSkipAuth && (!explicitAuthorizationHeader || shouldUseFreshSessionAuth)) {
        token = await this.getAuthToken();
      }

      const url = /^https?:\/\//.test(endpoint)
        ? endpoint
        : endpoint.startsWith('/api/')
          ? `${this.baseUrl.replace(/\/api\/v2$/, '')}${endpoint}`
          : `${this.baseUrl}${endpoint}`;
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...(options.headers as Record<string, string>),
      };

      // Only add custom headers if explicitly enabled (to avoid CORS issues)
      if (this.enableCustomHeaders) {
        headers['X-Request-ID'] = crypto.randomUUID();
        headers['X-Client-Version'] = '1.0.0';
      }

      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const doctorControlRequestId =
        options.doctorControlRequestId ?? getDoctorControlRequestIdFromStorage();
      const shouldAttachDoctorControlHeader = !options.skipDoctorControlHeader;
      if (doctorControlRequestId && shouldAttachDoctorControlHeader) {
        headers['x-doctor-control-request-id'] = doctorControlRequestId;
      }

      const {
        cache: _cache,
        timeout: _timeout,
        retries: _retries,
        priority: _priority,
        responseType: _responseType,
        skipAuth: _skipAuth,
        skipProactiveTokenRefresh: _skipProactiveTokenRefresh,
        useFreshSessionAuth: _useFreshSessionAuth,
        timeoutProfile: _timeoutProfile,
        dedupe: _dedupe,
        requestLabel: _requestLabel,
        doctorControlRequestId: _doctorControlRequestId,
        skipDoctorControlHeader: _skipDoctorControlHeader,
        ...fetchOptions
      } = options;

      const config: RequestInit = {
        ...fetchOptions,
        headers,
        signal: requestSignal.signal,
      };

      const response = await fetch(url, config);
      if (!response || typeof response.ok !== 'boolean') {
        throw new TypeError('Invalid fetch response');
      }

      // Log performance metrics
      const duration = Date.now() - startTime;
      logger.performanceMetric('api_request_duration', duration, 'ms');
      recordSlowApi(endpoint, options.method || 'GET', duration);

      if (!response.ok) {
        return await this.handleErrorResponse<T>(response, endpoint, options, retryCount);
      }

      // Handle empty responses (like 204 No Content)
      if (response.status === 204) {
        return {} as T;
      }

      const contentType = response.headers?.get?.('content-type') || '';
      const requestedResponseType = options.responseType;
      const canReadBlob =
        typeof (response as Response & { blob?: () => Promise<Blob> }).blob === 'function';
      const canReadJson =
        typeof (response as Response & { json?: () => Promise<T> }).json === 'function';

      let data: T;
      if (
        requestedResponseType === 'blob' ||
        (!requestedResponseType &&
          canReadBlob &&
          !canReadJson &&
          !contentType.includes('application/json') &&
          !contentType.includes('+json'))
      ) {
        data = (await response.blob()) as T;
      } else if (requestedResponseType === 'text') {
        data = (await response.text()) as T;
      } else {
        data = await response.json();
      }

      return data;
    } catch (error) {
      const duration = Date.now() - startTime;
      recordSlowApi(endpoint, method, duration);

      if (error instanceof ApiClientError) {
        const logMeta = {
          endpoint,
          requestLabel,
          method,
          duration,
          error: error.message,
          status: error.status,
          code: error.code,
          kind: error.kind,
        };
        if (
          this.shouldWarnForRecoverableAuthContextFailure(
            endpoint,
            requestLabel,
            error.kind,
            error.status
          )
        ) {
          logger.warn('API request failed', logMeta);
        } else {
          logger.error('API request failed', logMeta);
        }
        throw error;
      }

      // Handle timeout errors
      if (
        requestSignal.didTimeout() ||
        (error instanceof DOMException && error.name === 'TimeoutError')
      ) {
        const logMeta = { endpoint, requestLabel, timeout, duration };
        if (
          this.shouldWarnForRecoverableAuthContextFailure(endpoint, requestLabel, 'timeout', 408)
        ) {
          logger.warn('API request timeout', logMeta);
        } else {
          logger.error('API request timeout', logMeta);
        }
        throw new ApiClientError(
          `Request timeout after ${timeout}ms`,
          408,
          'REQUEST_TIMEOUT',
          undefined,
          'timeout',
          true
        );
      }

      // Handle abort errors as expected cancellation so query libraries can
      // treat them as cancelled work instead of terminal failures.
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw error;
      }

      // Network or other errors
      logger.error('Network error', { endpoint, duration, error });
      throw new ApiClientError(
        'Network error or server unavailable',
        0,
        'NETWORK_ERROR',
        undefined,
        'network',
        true
      );
    } finally {
      requestSignal.cleanup();
    }
  }

  private async handleErrorResponse<T>(
    response: Response,
    endpoint: string,
    options: RequestConfig,
    retryCount: number
  ): Promise<T> {
    let errorData: ApiError;

    try {
      errorData = await response.json();
    } catch {
      // If we can't parse the error response, create a generic one
      errorData = {
        error: {
          code: 'UNKNOWN_ERROR',
          message: `HTTP ${response.status}: ${response.statusText}`,
        },
      };
    }

    const { error } = errorData;

    if (
      error.message?.toLowerCase().includes('doctor control session') ||
      error.message?.toLowerCase().includes('doctor control request')
    ) {
      clearDoctorControlStorage();
    }

    if (response.status === 429) {
      logger.warn('Rate limit exceeded; request will not auto-retry', {
        endpoint,
        retryAfterMs: parseRetryAfterMs(response.headers.get('Retry-After'), 0),
      });
    }

    // Handle authentication errors by attempting token refresh and retry
    if (response.status === 401 && !options.skipAuth && retryCount < 1) {
      try {
        logger.info('401 error, attempting token refresh and retry', { endpoint });

        const { data, error: refreshError } = await this.withTimeout(
          refreshOwnedSession(),
          8000,
          'Token refresh after 401'
        );

        if (refreshError || !data.session) {
          if (refreshError && this.isInvalidRefreshTokenError(refreshError)) {
            await this.clearInvalidSession('retry_after_401_failed', refreshError);
          } else {
            logger.error('Token refresh failed', refreshError, {
              stage: 'handleErrorResponse',
              endpoint,
            });
          }
          throw new ApiClientError(
            'Authentication required. Please sign in again.',
            401,
            'AUTHENTICATION_REQUIRED',
            undefined,
            'auth',
            false
          );
        }

        // Retry the request with the new token
        return await this.executeRequest<T>(endpoint, options, retryCount + 1);
      } catch (error) {
        if (error instanceof ApiClientError) {
          throw error;
        }

        throw new ApiClientError(
          'Authentication required. Please sign in again.',
          401,
          'AUTHENTICATION_REQUIRED',
          undefined,
          'auth',
          false
        );
      }
    }

    const kind = classifyApiErrorFromStatus(response.status, error.code);
    throw new ApiClientError(
      error.message || 'An error occurred',
      response.status,
      error.code,
      error.details,
      kind,
      isRetryableErrorKind(kind)
    );
  }

  // HTTP Methods
  async get<T>(
    endpoint: string,
    params?: Record<string, any>,
    config?: Omit<RequestConfig, 'method' | 'body'>
  ): Promise<T> {
    let finalEndpoint = endpoint;

    if (params) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach(item => {
            if (item !== undefined && item !== null) searchParams.append(key, String(item));
          });
        } else if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
      const queryString = searchParams.toString();
      if (queryString) {
        finalEndpoint += (endpoint.includes('?') ? '&' : '?') + queryString;
      }
    }

    return this.makeRequest<T>(finalEndpoint, {
      method: 'GET',
      ...config,
    });
  }

  async post<T>(endpoint: string, data?: any, config?: Omit<RequestConfig, 'method'>): Promise<T> {
    return this.makeRequest<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
      ...config,
    });
  }

  async patch<T>(endpoint: string, data?: any, config?: Omit<RequestConfig, 'method'>): Promise<T> {
    return this.makeRequest<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
      ...config,
    });
  }

  async put<T>(endpoint: string, data?: any, config?: Omit<RequestConfig, 'method'>): Promise<T> {
    return this.makeRequest<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
      ...config,
    });
  }

  async delete<T>(endpoint: string, config?: Omit<RequestConfig, 'method'>): Promise<T> {
    return this.makeRequest<T>(endpoint, {
      method: 'DELETE',
      ...config,
    });
  }

  // Batch request method for multiple API calls
  async batch(
    requests: Array<{
      endpoint: string;
      method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
      data?: any;
      params?: Record<string, any>;
      config?: RequestConfig;
    }>
  ): Promise<any[]> {
    const promises = requests.map(({ endpoint, method = 'GET', data, params, config }) => {
      switch (method) {
        case 'GET':
          return this.get(endpoint, params, config);
        case 'POST':
          return this.post(endpoint, data, config);
        case 'PUT':
          return this.put(endpoint, data, config);
        case 'PATCH':
          return this.patch(endpoint, data, config);
        case 'DELETE':
          return this.delete(endpoint, config);
        default:
          throw new Error(`Unsupported method: ${method}`);
      }
    });

    return Promise.all(promises);
  }

  // Optimistic update helper
  async optimisticUpdate<T>(endpoint: string, data: any, rollbackFn?: () => void): Promise<T> {
    try {
      const result = await this.put<T>(endpoint, data);
      return result;
    } catch (error) {
      // Rollback on error
      if (rollbackFn) {
        rollbackFn();
      }
      throw error;
    }
  }

  // File upload helper (for presigned URLs)
  async uploadFile(presignedUrl: string, file: File | Blob, contentType: string): Promise<void> {
    const response = await fetch(presignedUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': contentType,
      },
      body: file,
    });

    if (!response.ok) {
      throw new ApiClientError('File upload failed', response.status, 'UPLOAD_FAILED');
    }
  }
}

export const apiClient = new ApiClient();
